import moment from 'moment';
import momentTimezone from 'moment-timezone';

/**
 * Parse the time in the "17:00:00" format
 *
 * @param  {string} s  Machine time string
 *
 * @return {object}    Moment object
 */
export const parseTime = (s) => moment(s, 'HH:mm:ss');


/**
 * Render the time in the "9:30pm" format
 *
 * @param  {object} t  Moment object
 *
 * @return {string}    Human-readable time string
 */
export const renderTime12 = (t) => moment(t, 'HH:mm:ss').format('h:mma');


/**
 * Convert a date to New Zealand timezone (Pacific/Auckland)
 * Handles dates with timezone info (e.g., +13:00, +12:00) correctly
 *
 * @param  {string|Date} date  Date string or Date object
 *
 * @return {object}            Moment object in Pacific/Auckland timezone
 */
export function momentNZ(date) {
    // Parse the date first - momentTimezone will correctly parse timezone offsets
    // (e.g., +13:00, +12:00) from the date string
    // Then convert to Pacific/Auckland timezone
    const parsed = momentTimezone(date);

    return parsed.tz('Pacific/Auckland');
}


/**
 * Convert a date to UTC timezone for comparison purposes
 * Handles dates with timezone info (e.g., +13:00, +12:00) correctly
 *
 * @param  {string|Date} date  Date string or Date object
 *
 * @return {object}            Moment object in UTC timezone at start of day
 */
export function momentUTC(date) {
    // Parse the date first (handles timezone info in date string)
    // Then convert to UTC and normalize to start of day
    const parsed = momentTimezone(date);

    return parsed.utc().startOf('day');
}


/**
 * Get today's date in UTC timezone at start of day (for comparison)
 *
 * @return {object}    Moment object in UTC timezone at start of day
 */
export function todayUTC() {
    return momentTimezone().utc().startOf('day');
}


/**
 * Format event/exhibition date range for display
 *
 * @param  {string} startDate  Start date string
 * @param  {string} endDate    End date string
 *
 * @return {string}            Formatted date range (e.g., "21 Feb 2024", "21 Feb – 27 Feb 2024", "21 Feb 2024 – 17 Mar 2026")
 */
export const formatEventExhibitionDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) {
        return '';
    }

    // Convert to NZ timezone and normalize to start of day
    const startMoment = momentNZ(startDate).startOf('day');
    const endMoment = momentNZ(endDate).startOf('day');

    // Return the formatted date (not the range) if the start and end dates are the same
    if (endMoment.isSame(startMoment, 'day')) {
        return endMoment.format('D MMM YYYY');
    }

    // Return the full date range with years
    return `${startMoment.format('D MMM YYYY')} – ${endMoment.format('D MMM YYYY')}`;
};

